/**
 * Password-manager & autofill suppression for ordinary app text fields.
 *
 * Third-party password managers (1Password, LastPass, Bitwarden, Dashlane,
 * Proton Pass) each honor a vendor-specific `data-*` attribute that tells them
 * to leave a field alone. We stamp all of them onto non-credential text inputs
 * so the app's document-name / search / profile fields don't sprout an autofill
 * icon or a "save password?" prompt.
 *
 * Scope is enforced two independent ways (see `useSuppressPasswordManagers`):
 *   1. The observer is started ONLY inside the authenticated route tree, so
 *      login / signup / reset-password (the UnAuthenticatedRoutes tree) are
 *      never touched — password managers work normally there.
 *   2. `type="password"` (and other credential/non-text types) are always
 *      skipped here, so even in-app password fields (UpdatePassword, PIN entry)
 *      keep working with managers. Add the `data-allow-pw` attribute to opt an
 *      individual field out of suppression entirely.
 *
 * We intentionally do NOT observe `type` attribute changes: a "show password"
 * toggle briefly flips a password field to `type="text"`, and stamping it in
 * that window would leave the ignore attributes on a genuine credential field.
 * Inputs are evaluated once, at the moment they enter the DOM.
 */

/** Attribute a developer can add to a field to opt it out of suppression. */
export const PW_MANAGER_OPT_OUT_ATTR = "data-allow-pw";

/** Vendor-documented "ignore this field" attributes, one per major manager. */
export const PW_MANAGER_IGNORE_ATTRS: Readonly<Record<string, string>> = {
    "data-1p-ignore": "", // 1Password
    "data-lpignore": "true", // LastPass
    "data-bwignore": "", // Bitwarden
    "data-form-type": "other", // Dashlane
    "data-protonpass-ignore": "", // Proton Pass
};

/**
 * Input types that are NOT ordinary text fields — either credential fields we
 * must leave to the password manager (`password`), or non-text controls a
 * manager never targets anyway. `HTMLInputElement.type` resolves an absent or
 * unknown type to `"text"`, so this set is exhaustive for our purposes.
 */
const NON_TEXT_INPUT_TYPES = new Set([
    "password",
    "hidden",
    "checkbox",
    "radio",
    "file",
    "submit",
    "reset",
    "button",
    "image",
    "range",
    "color",
]);

/** True when `el` is an ordinary text input we should suppress managers on. */
export function shouldSuppress(el: Element): el is HTMLInputElement {
    if (!(el instanceof HTMLInputElement)) return false;
    if (el.hasAttribute(PW_MANAGER_OPT_OUT_ATTR)) return false;
    if (NON_TEXT_INPUT_TYPES.has(el.type)) return false;
    // Leave credential forms alone. A text/email input that shares a <form> with a
    // password field is the username half of a login (e.g. the Shepherd export modal's
    // email + password, or the login page itself) — a password manager needs to fill
    // BOTH halves, so suppressing the username breaks credential autofill. Standalone
    // fields (document name, search) have no password sibling and are still suppressed.
    if (el.form?.querySelector('input[type="password"]')) return false;
    return true;
}

/**
 * Stamp the ignore attributes on one input. Idempotent, and never clobbers an
 * attribute a developer set intentionally (e.g. `autocomplete="new-password"`
 * or an explicit `data-lpignore` override).
 */
export function stampInput(el: HTMLInputElement): void {
    for (const [name, value] of Object.entries(PW_MANAGER_IGNORE_ATTRS)) {
        if (!el.hasAttribute(name)) el.setAttribute(name, value);
    }
    if (!el.hasAttribute("autocomplete")) el.setAttribute("autocomplete", "off");
}

/** Stamp every suppressible input within `root` (the root itself included). */
export function stampWithin(root: ParentNode | Element): void {
    if (root instanceof Element && shouldSuppress(root)) stampInput(root);
    root.querySelectorAll?.("input").forEach((el) => {
        if (shouldSuppress(el)) stampInput(el);
    });
}

/**
 * Start suppression: stamp everything currently under `root`, then watch for
 * inputs added later (React re-renders, modals, lazily-loaded routes). Returns
 * a disconnect function — call it when the authenticated tree unmounts.
 */
export function observePasswordManagerSuppression(root: HTMLElement = document.body): () => void {
    stampWithin(root);

    const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            mutation.addedNodes.forEach((node) => {
                if (node instanceof Element) stampWithin(node);
            });
        }
    });

    observer.observe(root, { childList: true, subtree: true });

    return () => observer.disconnect();
}
